import { graphql } from 'gatsby';
import { Paper, Heading } from 'lucid-components-react';
import React, { useEffect, useRef, useState } from 'react';

import {
  Drupal_FieldParagraphNineteenArticleFieldNineteenCtas,
  Drupal_FieldParagraphNineteenHighlightsFieldNineteenArticles,
  Drupal_ParagraphNineteenArticle,
  Drupal_ParagraphNineteenHighlights
} from '../../../generated/graphql';
import { getProcessedValue } from '../../../utils/helpers';
import NineteenCta, { nineteenCtaThemes } from '../nineteen-cta/nineteen-cta';
import './nineteen-highlights.scss';
import {
  sendBeaconEvent,
  MarketingPageElementLocation,
  MarketingPageElementType
} from '@lucid/gatsby-theme-lucid';
import LazyLoad from 'react-lazyload';

interface Props {
  entity: Drupal_ParagraphNineteenHighlights;
}

const NineteenHighlights = ({
  entity: { fieldHeader, fieldNineteenArticles }
}: Props) => {
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);
  const chosenArticle = (fieldNineteenArticles as Drupal_FieldParagraphNineteenHighlightsFieldNineteenArticles[])[
    activeSectionIndex
  ];
  const chosenArticleEntity = chosenArticle
    ? (chosenArticle.entity as Drupal_ParagraphNineteenArticle)
    : null;

  const isHighlightClicked = useRef(false);
  const thisHighlight = useRef(null);

  useEffect(() => {
    if (isHighlightClicked.current && window.innerWidth <= 500) {
      const current = (thisHighlight.current as unknown) as HTMLElement;

      const activeHighlight = current.querySelector(
        '.nineteen-highlights__list-item--active'
      );

      if (activeHighlight) {
        activeHighlight.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [activeSectionIndex]);

  // Unselect the default active section on tablet and lower
  useEffect(() => {
    if (window.innerWidth <= 900) {
      setActiveSectionIndex(-1);
    }
  }, []);

  const getHighlightContent = (
    articleEntity: Drupal_ParagraphNineteenArticle,
    isMobile: boolean
  ) => (
    <Paper
      variant="raised"
      className={`nineteen-highlights__body ${
        isMobile ? 'nineteen-highlights__body--mobile' : ''
      }`}
    >
      <button
        type="button"
        className="nineteen-highlights__close"
        onClick={() => setActiveSectionIndex(-1)}
      >
        ✕
      </button>
      <div className="nineteen-highlights__contents">
        <Heading
          as="h1"
          variant="subheadS"
          className="nineteen-highlights__content-header"
        >
          {articleEntity.fieldHeading}
        </Heading>
        {articleEntity.fieldSubheader && (
          <p className="nineteen-highlights__content-subheader">
            {articleEntity.fieldSubheader}
          </p>
        )}
        <div
          className="nineteen-highlights__copy"
          dangerouslySetInnerHTML={{
            __html: getProcessedValue(articleEntity.fieldBody)
          }}
        />
        {(articleEntity.fieldNineteenCtas as Drupal_FieldParagraphNineteenArticleFieldNineteenCtas[]).map(
          ({ entity }) => (
            <NineteenCta
              theme={nineteenCtaThemes.arrow}
              key={entity!.entityId!}
              entity={entity!}
            />
          )
        )}
      </div>
      <div className="nineteen-highlights__image-wrapper">
        <LazyLoad offset={200}>
          <img
            src={articleEntity.fieldImage!}
            alt={articleEntity.fieldAltText!}
            className="nineteen-highlights__image"
          />
        </LazyLoad>
      </div>
    </Paper>
  );

  return (
    <section className="nineteen-highlights" ref={thisHighlight}>
      <div className="lucid-container">
        {fieldHeader && (
          <Heading as="h2" variant="subheadS" mb={8}>
            {fieldHeader}
          </Heading>
        )}
        <ul
          className={`nineteen-highlights__list  ${
            activeSectionIndex >= 0
              ? 'nineteen-highlights__list--has-choice'
              : ''
          }`}
        >
          {(fieldNineteenArticles as Drupal_FieldParagraphNineteenHighlightsFieldNineteenArticles[]).map(
            (article, i) => {
              const entity = article.entity as Drupal_ParagraphNineteenArticle;
              return (
                <li
                  key={entity.entityId!}
                  className={`nineteen-highlights__list-item  ${
                    i === activeSectionIndex
                      ? 'nineteen-highlights__list-item--active'
                      : ''
                  }`}
                >
                  <button
                    type="button"
                    className="nineteen-highlights__list-button"
                    onClick={() => {
                      if (
                        window.screen.width <= 900 &&
                        i === activeSectionIndex
                      ) {
                        isHighlightClicked.current = false;
                        setActiveSectionIndex(-1);
                      } else {
                        entity.fieldBeaconElementDetail &&
                          sendBeaconEvent({
                            name: 'clickedMarketingPageElement',
                            elementLocation: MarketingPageElementLocation.body,
                            elementDetail: entity.fieldBeaconElementDetail,
                            elementType: MarketingPageElementType.accordion
                          });
                        isHighlightClicked.current = true;
                        setActiveSectionIndex(i);
                      }
                    }}
                  >
                    <div className="nineteen-highlights__list-thumb">
                      <LazyLoad offset={200}>
                        <img
                          src={entity.fieldImage!}
                          alt={entity.fieldAltText!}
                          className="nineteen-highlights__list-image"
                        />
                      </LazyLoad>
                    </div>
                    <p className="nineteen-highlights__list-text">
                      {entity.fieldHeading}
                    </p>
                  </button>
                  {chosenArticleEntity &&
                    getHighlightContent(chosenArticleEntity, true)}
                </li>
              );
            }
          )}
        </ul>
        {chosenArticleEntity && getHighlightContent(chosenArticleEntity, false)}
      </div>
    </section>
  );
};

export default NineteenHighlights;

export const query = graphql`
  fragment nineteenHighlights on Drupal_ParagraphNineteenHighlights {
    fieldHeader
    fieldNineteenArticles {
      entity {
        ...nineteenArticle
      }
    }
  }
`;
